import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  PageTheme,
  UnifiedTheme,
  UnifiedThemeOptions,
} from '@backstage/theme';
import tokens from '../../components/ui/foundation.tokens.json';

/**
 * Regask portal MUI theme — derived from the SINGLE SOURCE OF TRUTH
 * (components/ui/foundation.tokens.json), the same tokens the Tailwind design
 * system uses. So MUI-rendered chrome (page backgrounds, sidebar, any
 * not-yet-converted components) matches the Tailwind surfaces with no
 * duplicated values. Dark-first, blue brand. The `teal`/`tealBright` keys
 * keep their names for compatibility but hold the brand blue.
 */
const { palette, semantic } = tokens;

type SemanticTokens = (typeof semantic)['dark'];

/** Map the foundation's semantic tokens onto the kit shape the theme uses. */
function variantKit(s: SemanticTokens, surfaceElevated: string) {
  return {
    canvas: s.app,
    surface: s.surface,
    surfaceElevated,
    border: s.border,
    teal: s.brand,
    tealBright: s.brandHover,
    textPrimary: s.fg,
    textSecondary: s.fgTertiary,
    success: s.success,
    warning: s.warning,
    error: s.error,
    info: s.info,
  };
}

export const uiKit = {
  dark: variantKit(semantic.dark, palette.gray['800']),
  light: variantKit(semantic.light, palette.gray['100']),
  /** Brand-tinted fill for the selected sidebar item (both variants). */
  selectedTeal: palette.brand['700'],
  /** Sidebar surface, dark in both variants. */
  sidebar: semantic.dark.app,
} as const;

/**
 * Regask UI type system (from the same single source). Inter carries the
 * interface and display type; Geist Mono carries machine data so numbers line
 * up and read as data. Exposed so the shared UI primitives reference the same
 * families rather than re-declaring stacks.
 */
export const fontFamily = tokens.fonts.sans;
export const monoFontFamily = tokens.fonts.mono;

/**
 * Kit-style sidebar. Foundation tokens (not static hex) so it follows BOTH
 * light/dark mode and the selected palette — these are the colors the mobile
 * sidebar (BottomNavigation + overlay) reads directly. `navigation` is a custom
 * palette key, so MUI runs no color math on it and `var(...)` is safe here.
 */
const navigation = {
  background: 'var(--c-surface)',
  indicator: 'var(--c-brand)',
  color: 'var(--c-fg-secondary)',
  selectedColor: 'var(--c-fg)',
  navItem: {
    hoverBackground: 'var(--c-muted-strong)',
  },
  submenu: {
    background: 'var(--c-surface)',
  },
};

/**
 * Flat page theme: no gradient/burst image, and the page background is the
 * canvas so Backstage `Page` surfaces match the body / design-system canvas
 * (cards and PageHeader provide their own surface on top).
 */
function flatPageTheme(bg: string, fontColor: string): PageTheme {
  return {
    ...genPageTheme({ colors: [bg, bg], shape: shapes.wave }),
    backgroundImage: 'none',
    fontColor,
  };
}

function buildPageThemes(bg: string, fontColor: string) {
  const flat = flatPageTheme(bg, fontColor);
  return {
    home: flat,
    app: flat,
    apis: flat,
    documentation: flat,
    tool: flat,
    service: flat,
    website: flat,
    library: flat,
    other: flat,
  };
}

type KitVariant = (typeof uiKit)['dark'] | (typeof uiKit)['light'];

/**
 * Component overrides shared by both variants (kit look: 10–12px card
 * radius, 1px hairline borders, no heavy shadows, flat headers, compact
 * uppercase table headers). The sidebar item styling lives in the custom
 * icon rail (modules/nav/Sidebar.tsx), not in a theme override.
 */
function buildComponents(
  t: KitVariant,
  nodeLabelFill: string,
): UnifiedThemeOptions['components'] {
  return {
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: 'none',
          backgroundColor: 'var(--c-surface)',
          boxShadow: 'none',
          borderBottom: '1px solid var(--c-border)',
          paddingTop: 16,
          paddingBottom: 16,
        },
        title: {
          fontWeight: 600,
          fontSize: 24,
        },
        subtitle: {
          color: 'var(--c-fg-secondary)',
        },
        // The small kind/type line above the entity page title.
        type: {
          color: 'var(--c-fg-secondary)',
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase' as const,
          letterSpacing: 0.5,
        },
      },
    },
    // Entity page tab bar (new-FE EntityTabsList renders core-components
    // HeaderTabs classes): flat surface bar with a hairline bottom border,
    // sentence-case gray labels, white text + teal underline (the Tabs
    // indicator is palette.primary) when active.
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: 'var(--c-surface)',
          borderBottom: '1px solid var(--c-border)',
        },
        defaultTab: {
          textTransform: 'none' as const,
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: 0,
          color: 'var(--c-fg-secondary)',
          padding: '14px 16px',
          minWidth: 0,
        },
        selected: {
          color: 'var(--c-fg)',
        },
        tabRoot: {
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'var(--c-fg)',
          },
        },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          // Foundation tokens (not the static variant hex) so the rail follows
          // BOTH the light/dark mode and the selected palette theme — the
          // `--c-*` vars are re-pointed by `[data-palette]` in tailwind.css.
          background: 'var(--c-surface)',
          borderRight: '1px solid var(--c-border)',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: 32,
          lineHeight: 1.15,
          fontWeight: 700,
          letterSpacing: -0.8,
        },
        h2: {
          fontSize: 24,
          lineHeight: 1.2,
          fontWeight: 700,
          letterSpacing: -0.45,
        },
        h3: {
          fontSize: 20,
          lineHeight: 1.25,
          fontWeight: 700,
          letterSpacing: -0.25,
        },
        h4: {
          fontSize: 18,
          lineHeight: 1.3,
          fontWeight: 650,
          letterSpacing: -0.15,
        },
        h5: {
          fontSize: 16,
          lineHeight: 1.35,
          fontWeight: 650,
        },
        h6: {
          fontSize: 14,
          lineHeight: 1.4,
          fontWeight: 650,
        },
        subtitle1: {
          fontSize: 14,
          lineHeight: 1.55,
          color: 'var(--c-fg-secondary)',
        },
        subtitle2: {
          fontSize: 13,
          lineHeight: 1.5,
          color: 'var(--c-fg-secondary)',
          fontWeight: 600,
        },
        body1: {
          fontSize: 14,
          lineHeight: 1.6,
        },
        body2: {
          fontSize: 13,
          lineHeight: 1.55,
        },
        caption: {
          fontSize: 12,
          lineHeight: 1.45,
          color: 'var(--c-fg-secondary)',
        },
        button: {
          fontSize: 13,
          fontWeight: 650,
          letterSpacing: 0,
          textTransform: 'none' as const,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: 'var(--c-brand-text)',
          fontWeight: 500,
          textUnderlineOffset: 3,
          transition: 'color 140ms ease',
          '&:hover': {
            color: 'var(--c-brand-hover)',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'var(--c-fg-secondary)',
          fontSize: 13,
          fontWeight: 600,
          '&$focused': {
            color: 'var(--c-brand-text)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 6,
          fontSize: 12,
          lineHeight: 1.45,
          color: 'var(--c-fg-secondary)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: '1px solid var(--c-border)',
          boxShadow:
            t === uiKit.dark
              ? '0 18px 50px rgba(0, 0, 0, 0.22)'
              : '0 18px 45px rgba(15, 23, 42, 0.06)',
          // No gradient overlay — the light-mode variant was a near-opaque white
          // wash that painted over the palette surface (card looked white on any
          // palette). Let the var(--c-surface) backgroundColor show through.
          backgroundColor: 'var(--c-surface)',
          backgroundImage: 'none',
          overflow: 'hidden',
        },
      },
    },
    // ItemCardGrid (e.g. the container cards in the Kubernetes pod drawer)
    // uses `auto-fill`, which keeps empty grid tracks so a lone card stays
    // pinned to the ~22em min width — leaving the card cramped and the rest
    // of the wide drawer empty. `auto-fit` collapses the empty tracks so a
    // single card stretches to full width (multi-card grids are unchanged).
    BackstageItemCardGrid: {
      styleOverrides: {
        root: {
          gridTemplateColumns: 'repeat(auto-fit, minmax(22em, 1fr))',
        },
      },
    },
    // Stock InfoCards (Settings page Profile/Appearance/Identity cards and
    // any other plugin card): align the header with our own card idiom —
    // 20px padding on the kit scale and a 16px/600 title instead of the
    // oversized stock h5.
    BackstageInfoCard: {
      styleOverrides: {
        header: {
          padding: '20px 20px 12px',
        },
        headerTitle: {
          fontSize: 16,
          fontWeight: 600,
        },
        headerSubheader: {
          paddingTop: 4,
          fontSize: 13,
          color: 'var(--c-fg-secondary)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        // Surface token (not palette.background.paper, which stays hex) so every
        // stock Paper-backed surface — Backstage InfoCard/Table, the ArgoCD and
        // other plugin entity cards, scaffolder/workflow forms — follows the
        // palette. Raw CSS override, so no color math runs.
        root: {
          backgroundColor: 'var(--c-surface)',
          // Kill any elevation-overlay gradient so the palette surface is the
          // only thing painting the card.
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow:
            t === uiKit.dark
              ? '0 16px 44px rgba(0, 0, 0, 0.20)'
              : '0 16px 40px rgba(15, 23, 42, 0.06)',
          border: '1px solid var(--c-border)',
          backgroundImage: 'none',
        },
        elevation2: {
          boxShadow:
            t === uiKit.dark
              ? '0 20px 54px rgba(0, 0, 0, 0.24)'
              : '0 20px 48px rgba(15, 23, 42, 0.08)',
          border: '1px solid var(--c-border)',
          backgroundImage: 'none',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          WebkitTapHighlightColor: 'transparent',
        },
      },
    },
    // Stock loading indicators (Backstage <Progress> is an indeterminate
    // LinearProgress; plugins use CircularProgress) tint from the brand token
    // so they follow the palette instead of the static palette.primary hex.
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: 'var(--c-muted-strong)',
        },
        barColorPrimary: {
          backgroundColor: 'var(--c-brand)',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: 'var(--c-brand)',
        },
      },
    },
    // Stepper dots (scaffolder/workflow wizard) — the active/completed StepIcon
    // fills from palette.primary (static hex); tint from the brand token so they
    // follow the palette instead of the default blue.
    MuiStepIcon: {
      styleOverrides: {
        // Must be nested in `root` to match MUI's own compound-selector
        // specificity (overriding the `active`/`completed` slots directly is
        // rejected with a warning). Both the v4 (`.MuiStepIcon-active`) and v5
        // (`.Mui-active`) class names are covered.
        root: {
          '&.MuiStepIcon-active, &.Mui-active': {
            color: 'var(--c-brand)',
          },
          '&.MuiStepIcon-completed, &.Mui-completed': {
            color: 'var(--c-brand)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none' as const,
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          color: 'var(--c-on-brand)',
          background:
            'linear-gradient(135deg, var(--c-brand), var(--c-brand-hover))',
        },
        outlined: {
          borderColor: 'var(--c-border)',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition:
            'border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease',
        },
        input: {
          '&::placeholder': {
            color: 'var(--c-fg-secondary)',
            opacity: 0.8,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor:
            t === uiKit.dark ? 'rgba(255,255,255,0.035)' : '#FFFFFF',
          '&:hover $notchedOutline': {
            borderColor: 'var(--c-brand)',
          },
          '&$focused $notchedOutline': {
            borderColor: 'var(--c-brand)',
            boxShadow: `0 0 0 3px ${t.teal}26`,
          },
        },
        notchedOutline: {
          borderColor: 'var(--c-border)',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            borderRadius: 12,
            backgroundColor: 'transparent',
          },
        },
        icon: {
          color: 'var(--c-fg-secondary)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 6,
          borderRadius: 14,
          border: '1px solid var(--c-border)',
          backgroundColor: 'var(--c-surface)',
          boxShadow:
            t === uiKit.dark
              ? '0 22px 60px rgba(0,0,0,0.36)'
              : '0 22px 60px rgba(15,23,42,0.12)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: 8,
          margin: '2px 6px',
          fontSize: 13,
          '&:hover': {
            backgroundColor:
              t === uiKit.dark ? 'rgba(255,255,255,0.055)' : '#F3F4F6',
          },
          '&$selected': {
            color: 'var(--c-brand-text)',
            backgroundColor: `${t.teal}1F`,
          },
          '&$selected:hover': {
            backgroundColor: `${t.teal}29`,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: 12,
          backgroundColor: t === uiKit.dark ? '#27272A' : '#18181B',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
        sizeSmall: {
          borderRadius: 6,
        },
      },
    },
    // Kit-style table headers: compact uppercase 11px secondary text.
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'var(--c-border)',
        },
        head: {
          textTransform: 'uppercase' as const,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: 'var(--c-fg-secondary)',
          // `!important` to beat material-table's inline
          // `background: palette.background.paper` (hex) on the sticky header,
          // so the header matches the card surface and follows the palette.
          backgroundColor: 'var(--c-surface) !important',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: 52,
          transition: 'background-color 120ms ease',
          '&$hover:hover, &:hover': {
            backgroundColor:
              t === uiKit.dark ? 'rgba(255,255,255,0.025)' : '#FAFAFA',
          },
        },
        head: {
          height: 44,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Flat canvas — matches the design-system `--c-app` token (and the
          // Storybook background) exactly, so converted pages and the catalog
          // render on the same surface. Uses the `--c-app` token (not the
          // static variant hex) so the canvas follows the selected palette.
          backgroundColor: 'var(--c-app)',
          // Default body text also follows the palette (most stock text inherits
          // this; MUI's CssBaseline otherwise hardcodes palette.text.primary).
          color: 'var(--c-fg)',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          // Inter's default glyphs (double-story a, standard figures).
          fontFeatureSettings: 'normal',
        },
        '::selection': {
          backgroundColor: `${t.teal}55`,
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${t.textSecondary}55 transparent`,
        },
        '*::-webkit-scrollbar': {
          width: 10,
          height: 10,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: `${t.textSecondary}55`,
          borderRadius: 999,
          border: '3px solid transparent',
          backgroundClip: 'padding-box',
        },
      },
    },
    // Catalog relations graph (EntityCatalogGraphCard / @backstage/plugin-catalog-graph).
    // Stock nodes fill with palette.primary.light but the label + kind icon use
    // palette.primary.contrastText (white), unreadable on the blue brand fill.
    // The fill flips per theme (light periwinkle in dark mode, deeper indigo in
    // light mode), so the readable label color flips too: dark in dark mode,
    // white in light mode — passed in as `nodeLabelFill`.
    //
    // This MUST live here (theme.overrides), NOT as a CSS rule keyed off the
    // plugin's makeStyles class name. MUI v4 only emits the readable
    // `PluginCatalogGraphCustomNode-text-*` class in development; a production
    // build replaces it with an opaque `jss<n>`, so a `[class*='…']` selector
    // matches nothing once deployed. makeStyles merges `theme.overrides[name]`
    // at the JSS level (by rule key, not DOM class), so it survives the prod
    // build. The icon shares the `text` rule, so it gets the same fill.
    ...({
      PluginCatalogGraphCustomNode: {
        styleOverrides: {
          text: {
            '&.primary': { fill: nodeLabelFill },
            '&.secondary': { fill: nodeLabelFill },
          },
        },
      },
    } as UnifiedThemeOptions['components']),
  };
}

function buildPalette(
  base: typeof palettes.dark | typeof palettes.light,
  t: KitVariant,
): UnifiedThemeOptions['palette'] {
  return {
    ...base,
    primary: { main: t.teal },
    secondary: { main: t.tealBright },
    info: { main: t.info },
    success: { main: t.success },
    warning: { main: t.warning },
    error: { main: t.error },
    // Kept as static hex: MUI runs color math (alpha/darken) on these, which
    // throws on `var(...)`. The visible canvas follows the palette via the
    // MuiCssBaseline `body` background (flat pages are transparent), and the
    // sidebar via its own drawer override.
    background: {
      default: t.canvas,
      paper: t.surface,
    },
    // The whole `palette` block stays hex: createUnifiedTheme / MUI validate
    // and run color math (fade/darken) on these keys and throw on `var(...)`
    // ("MUI: Unsupported var(...) color"). Palette-following for stock chrome
    // is done in buildComponents (styleOverrides are raw CSS — no color math)
    // and via the navigation tokens.
    text: {
      primary: t.textPrimary,
      secondary: t.textSecondary,
    },
    divider: t.border,
    border: t.border,
    link: t.teal,
    linkHover: t.tealBright,
    status: {
      ...base.status,
      ok: t.success,
      warning: t.warning,
      error: t.error,
    },
    navigation,
  };
}

export const regaskDarkTheme: UnifiedTheme = createUnifiedTheme({
  fontFamily,
  palette: buildPalette(palettes.dark, uiKit.dark),
  defaultPageTheme: 'home',
  pageTheme: buildPageThemes(uiKit.dark.canvas, uiKit.dark.textPrimary),
  components: buildComponents(uiKit.dark, palette.gray['900']),
});

export const regaskLightTheme: UnifiedTheme = createUnifiedTheme({
  fontFamily,
  palette: buildPalette(palettes.light, uiKit.light),
  defaultPageTheme: 'home',
  pageTheme: buildPageThemes(uiKit.light.canvas, uiKit.light.textPrimary),
  components: buildComponents(uiKit.light, palette.base.white),
});
